import React from "react";
import {
  GridCsvExportOptions,
  GridExcelExportOptions,
  GridPrintExportOptions,
  useGridApiContext,
} from "@mui/x-data-grid-premium";

import {
  alpha,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";

import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TableViewOutlinedIcon from "@mui/icons-material/TableViewOutlined";
import PrintOutlinedIcon from "@mui/icons-material/PrintOutlined";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import IosShareOutlinedIcon from "@mui/icons-material/IosShareOutlined";

type Props = {
  label?: string;
  fileName?: string;
  csvOptions?: GridCsvExportOptions;
  excelOptions?: GridExcelExportOptions;
  printOptions?: GridPrintExportOptions;
  showCsv?: boolean;
  showExcel?: boolean;
  showPrint?: boolean;
  onExportAll?: () => void | Promise<void>;
  disabled?: boolean;
};

type Action = "csv" | "excel" | "print" | "all";

// FORMAT_META — aligned to theme palette
const FORMAT_META: Record<
  Exclude<Action, "all">,
  { ext: string; color: string; bg: string }
> = {
  csv: { ext: ".csv", color: "#1c6e8c", bg: "#E8F4F8" }, // theme primary
  excel: { ext: ".xlsx", color: "#2E7D32", bg: "#E8F5E9" }, // theme success (unchanged)
  print: { ext: "PDF", color: "#37474F", bg: "#ECEFF1" }, // unchanged
};

export default function GridExportMenu({
  label = "Eksport",
  fileName = `export_${new Date().toISOString().slice(0, 10)}`,
  csvOptions,
  excelOptions,
  printOptions,
  showCsv = true,
  showExcel = true,
  showPrint = true,
  onExportAll,
  disabled = false,
}: Props) {
  const apiRef = useGridApiContext();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [loadingAction, setLoadingAction] = React.useState<null | Action>(null);

  const open = Boolean(anchorEl);
  const isLoading = loadingAction !== null;

  const handleOpen = (e: React.MouseEvent<HTMLButtonElement>) =>
    setAnchorEl(e.currentTarget);
  const handleClose = () => setAnchorEl(null);

  const runAction = async (action: Action, cb: () => void | Promise<void>) => {
    handleClose();
    setLoadingAction(action);
    try {
      await cb();
    } finally {
      setLoadingAction(null);
    }
  };

  const handleExportCsv = () =>
    void runAction("csv", () =>
      apiRef.current.exportDataAsCsv({
        fileName,
        delimiter: ";",
        utf8WithBom: true,
        ...csvOptions,
      })
    );

  const handleExportExcel = () =>
    void runAction("excel", () =>
      apiRef.current.exportDataAsExcel({ fileName, ...excelOptions })
    );

  const handleExportPrint = () =>
    void runAction("print", () =>
      apiRef.current.exportDataAsPrint({
        hideFooter: true,
        hideToolbar: true,
        ...printOptions,
      })
    );

  const handleExportAll = () =>
    onExportAll && void runAction("all", onExportAll);

  const hasLocalExports = showCsv || showExcel || showPrint;

  return (
    <>
      {/* ── Trigger button ── */}
      <Button
        size="small"
        variant="text"
        onClick={handleOpen}
        disabled={disabled || isLoading}
        startIcon={
          isLoading ? (
            <CircularProgress size={14} thickness={5} />
          ) : (
            <IosShareOutlinedIcon sx={{ fontSize: 16 }} />
          )
        }
        endIcon={
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 18,
              transition: "transform 0.2s",
              transform: open ? "rotate(180deg)" : "rotate(0deg)",
            }}
          />
        }
        sx={{
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 500,
          fontSize: 13,
          px: 1,
          py: 0.625,
          letterSpacing: 0.2,
          color: "text.primary",
          "&:hover": {
            bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
          },
        }}
      >
        {label}
      </Button>

      {/* ── Dropdown menu ── */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionProps={{ timeout: 180 }}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              mt: 0.5,
              minWidth: 270,
              borderRadius: "12px",
              border: "1px solid",
              borderColor: "divider",
              overflow: "hidden",
              boxShadow:
                "0 4px 16px rgba(15,23,42,0.10), 0 1px 4px rgba(15,23,42,0.06)",
            },
          },
          list: {
            sx: {
              pb: 0.75,
            },
          },
        }}
      >
        {hasLocalExports
          ? [
              <Typography
                key="local-export-title"
                variant="caption"
                fontWeight={700}
                color="text.disabled"
                sx={{
                  px: 2,
                  pt: 1.5,
                  pb: 0.25,
                  display: "block",
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                Pobierz lokalnie
              </Typography>,

              showCsv ? (
                <ExportItem
                  key="export-csv"
                  label="CSV"
                  description="Wartości rozdzielone średnikiem"
                  ext=".csv"
                  iconColor={FORMAT_META.csv.color}
                  iconBg={FORMAT_META.csv.bg}
                  loading={loadingAction === "csv"}
                  onClick={handleExportCsv}
                  icon={<FileDownloadOutlinedIcon fontSize="small" />}
                />
              ) : null,

              showExcel ? (
                <ExportItem
                  key="export-excel"
                  label="Excel"
                  description="Skoroszyt Microsoft Excel"
                  ext=".xlsx"
                  iconColor={FORMAT_META.excel.color}
                  iconBg={FORMAT_META.excel.bg}
                  loading={loadingAction === "excel"}
                  onClick={handleExportExcel}
                  icon={<TableViewOutlinedIcon fontSize="small" />}
                />
              ) : null,

              showPrint ? (
                <ExportItem
                  key="export-print"
                  label="Drukuj / PDF"
                  description="Otwórz podgląd drukowania"
                  ext="PDF"
                  iconColor={FORMAT_META.print.color}
                  iconBg={FORMAT_META.print.bg}
                  loading={loadingAction === "print"}
                  onClick={handleExportPrint}
                  icon={<PrintOutlinedIcon fontSize="small" />}
                />
              ) : null,
            ]
          : null}

        {onExportAll
          ? [
              <Divider key="server-export-divider" sx={{ my: 0.5 }} />,

              <Typography
                key="server-export-title"
                variant="caption"
                fontWeight={700}
                color="text.disabled"
                sx={{
                  px: 2,
                  pt: 1,
                  pb: 0.25,
                  display: "block",
                  letterSpacing: 0.8,
                  textTransform: "uppercase",
                  fontSize: 10,
                }}
              >
                Eksport serwera
              </Typography>,

              <ExportItem
                key="export-all"
                label="Wszystkie wyniki"
                description="Pełny eksport przez backend"
                ext="ALL"
                iconColor="#6A1B9A"
                iconBg="#F3E5F5"
                loading={loadingAction === "all"}
                onClick={handleExportAll}
                icon={<CloudDownloadOutlinedIcon fontSize="small" />}
              />,
            ]
          : null}
      </Menu>
    </>
  );
}

// ── Sub-component: single export row ──────────────────────────────────────────
interface ExportItemProps {
  label: string;
  description: string;
  ext: string;
  iconColor: string;
  iconBg: string;
  loading: boolean;
  onClick: () => void;
  icon: React.ReactNode;
}

function ExportItem({
  label,
  description,
  ext,
  iconColor,
  iconBg,
  loading,
  onClick,
  icon,
}: ExportItemProps) {
  return (
    <MenuItem
      onClick={onClick}
      disabled={loading}
      sx={{
        px: 2,
        py: 1,
        mx: 0.75,
        my: 0.25,
        borderRadius: "10px",
        gap: 1.5,
        "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.06) },
        transition: "background 0.15s",
      }}
    >
      {/* Colored icon box */}
      <ListItemIcon sx={{ minWidth: 0 }}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            bgcolor: iconBg,
            color: iconColor,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {loading ? (
            <CircularProgress size={16} thickness={5} sx={{ color: iconColor }} />
          ) : (
            icon
          )}
        </Box>
      </ListItemIcon>

      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="body2" fontWeight={700} lineHeight={1.2}>
              {label}
            </Typography>
            <Chip
              label={ext}
              size="small"
              sx={{
                height: 18,
                fontSize: 10,
                fontWeight: 700,
                bgcolor: iconBg,
                color: iconColor,
                borderRadius: "5px",
                "& .MuiChip-label": { px: 0.75 },
              }}
            />
          </Box>
        }
        secondary={
          <Typography variant="caption" color="text.secondary" fontSize={11.5}>
            {description}
          </Typography>
        }
        disableTypography
      />
    </MenuItem>
  );
}